import IconTypes from '~/constants/types/IconTypes';
import CanvasTypes from '~/constants/types/CanvasTypes';
import {createAction} from 'redux-actions';
import uuid from 'uuid';
import {
  getClipboardIconIds,
  getClipboardSource,
  getColorsByIconIds,
  getCurrentDesignId,
  getLastIconColor,
  getSelectedIconIds,
  getSelectedIconsArray,
  getSelectedIconTypes,
  getUploadColors,
  getUploadIcons,
  selectedViewId,
  getIconById,
  viewUploadedIcons
} from '~/selectors/CanvasSelectors';
import {getInkMatches} from '~/selectors/DesignSelectors';
import {getIcons} from '~/selectors/IconSelectors';
import {
  isAnchorLaserEngraved,
  getLaserEngravedDesignColor,
  getView,
  getViewConstraint,
  getDecorationOverrideColor,
  getSelectedProductStyleType
} from '~/selectors/ProductSelectors';
import {UPLOAD_ONE_COLOR_FLAG} from '~/constants/FeatureFlags';
import {INK_COLORS} from '~/constants/InkConstants';
import {DEFAULT_CONTRAST} from '~/constants/UploadConstants';
import {changeView} from '~/actions/CanvasActions';
import {roundDecimal} from '~/shared/utils/math';
import {
  calculateConstraintsPixelsInches,
  getBiggestRect,
  getRectXDelta,
  getRectYDelta,
  getCoordsInInches
} from '~/utils/geometry';
import {
  getDimensionsForAspectRatio,
  getClampedIconDimensions
} from '~/utils/icon';
import {updateInkMatches} from './DesignActions';
import {error as errorNotification} from './NotificationActions';
import {COLOR_CHANGE_PANTONE_WARNING} from '~/constants/NotificationConstants';
import isEmpty from 'lodash/isEmpty';
import signaler from '~/vendor/signaler';
import {STYLE_TYPE} from '~/constants/ProductConstants';
import {getUploadAutoSizeDimension} from '~/data/accessors/canvas';
import {isUploadAutoSizingEnabled} from '~/selectors/ConfigSelectors';

export const align = createAction(IconTypes.ALIGN);

export const forward = payload => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  const iconIds = getSelectedIconIds(state);

  dispatch({
    type: IconTypes.FORWARD,
    payload: {
      ...payload,
      iconIds,
      designId
    }
  });
};

export const backward = payload => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  const iconIds = getSelectedIconIds(state);

  dispatch({
    type: IconTypes.BACKWARD,
    payload: {
      ...payload,
      iconIds,
      designId
    }
  });
};

export const forwardStep = payload => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  const iconIds = getSelectedIconIds(state);

  dispatch({
    type: IconTypes.FORWARD_STEP,
    payload: {
      ...payload,
      iconIds,
      designId
    }
  });
};

export const backwardStep = payload => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  const iconIds = getSelectedIconIds(state);

  dispatch({
    type: IconTypes.BACKWARD_STEP,
    payload: {
      ...payload,
      iconIds,
      designId
    }
  });
};

export const center = payload => (dispatch, getState) => {
  const state = getState();
  const iconIds = getSelectedIconIds(state);

  dispatch({
    type: IconTypes.CENTER,
    payload: {
      ...payload,
      iconIds
    }
  });
};

export const changeColor = createAction(
  IconTypes.CHANGE_COLOR,
  undefined,
  ({iconId, color, type}) => ({iconId, color: color.name, type})
);

export const copy = payload => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  const viewId = selectedViewId(state);

  // Retrieve selected icon ids from array of selected icons which is already
  // sorted respectfully to the layering
  const iconIds = getSelectedIconsArray(state).map(icon => icon.id);

  // Generate the map of source and target icon ids
  const iconsMap = iconIds.map(iconId => ({
    sourceIconId: iconId,
    sourceIconType: getIcons(state).get(iconId).type,
    targetIconId: uuid.v4()
  }));

  dispatch({
    type: IconTypes.COPY,
    payload: {
      ...payload,
      iconsMap,
      designId,
      viewId
    }
  });
};

export const crop =
  (icon, crop, sizeIsDirty = true) =>
  dispatch => {
    dispatch({
      type: IconTypes.CROP,
      payload: {
        iconId: icon.id,
        crop,
        sizeIsDirty
      }
    });
  };

export const cut = payload => (dispatch, getState) => {
  const state = getState();
  const designId = getCurrentDesignId(state);
  const iconIds = getSelectedIconIds(state);
  const viewId = selectedViewId(state);

  // Generate the map of source and target icon ids
  const iconsMap = iconIds.map(iconId => ({
    sourceIconId: iconId,
    sourceIconType: getIcons(state).get(iconId).type,
    targetIconId: uuid.v4()
  }));

  dispatch({
    type: IconTypes.CUT,
    payload: {
      ...payload,
      iconsMap,
      designId,
      viewId
    }
  });
};

export const paste =
  ({viewConstraint, viewId}) =>
  (dispatch, getState) => {
    const state = getState();
    const designId = getCurrentDesignId(state);
    const clipboardIconIds = getClipboardIconIds(state);
    const clipboardSource = getClipboardSource(state);
    // Generate ids for icons that are gonna be copied
    const iconsMap = clipboardIconIds.map(iconId => ({
      sourceIconId: iconId,
      targetIconId: uuid.v4()
    }));

    dispatch({
      type: IconTypes.PASTE,
      payload: {
        clipboardSource,
        designId,
        iconsMap,
        viewConstraint,
        viewId
      }
    });
  };

export const duplicate =
  (payload = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const designId = getCurrentDesignId(state);
    const selectedIcons = getSelectedIconsArray(state);
    const viewId = selectedViewId(state);
    const view = getView(state, {
      viewIdentifier: viewId
    });
    const viewConstraint = getViewConstraint(state)(view);
    const iconsMap = selectedIcons.map(iconId => ({
      sourceIconId: iconId.get('id'),
      targetIconId: uuid.v4()
    }));

    dispatch({
      type: IconTypes.DUPLICATE,
      payload: {
        designId,
        iconsMap,
        viewConstraint,
        viewId,
        source: payload.source,
        iconType: payload.iconType
      }
    });
  };

export const move = createAction(IconTypes.MOVE);

export const changePlacement =
  ({constraint, viewId, printScaleFactor, guidelines}) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedIcon = getSelectedIconsArray(state)[0];

    dispatch(changeView(viewId));

    let x, y, iconWidth, iconHeight;

    if (['Left Chest', 'Right Chest'].includes(constraint.name)) {
      // Grab the biggest guideline for the current view
      const biggestGuideline = getBiggestRect(guidelines);

      // Calculate the constraints of the guideline in pixels and inches
      const guidelineConstraints = calculateConstraintsPixelsInches({
        rect: biggestGuideline,
        printScaleFactor
      });

      // Converts the (left|top) coordinates in pixels to a coordinate (x,y) in a Cartesian system that uses 4 quadrants.
      // This system is used to place the icons on the Canvas based on the center at (0,0).
      const coordsInInches = getCoordsInInches({
        left: getRectXDelta(constraint) / 2 + constraint.left, // Add half of the width because the anchor is at the center of the rectangle
        top: getRectYDelta(constraint) / 2 + constraint.top, // Add half of the height because the anchor is at the center of the rectangle
        centerCoords: guidelineConstraints?.center?.pixels,
        printScaleFactor
      });
      x = roundDecimal(coordsInInches?.x, 4);
      y = roundDecimal(coordsInInches?.y, 4);

      // Clamps the icon dimensions to the constraint dimensions
      const clampedDimensions = getClampedIconDimensions({
        icon: selectedIcon,
        constraint
      });
      iconWidth = clampedDimensions?.width;
      iconHeight = clampedDimensions?.height;

      // Takes the image's aspect ratio into account and resize the smaller dimension based on the bigger dimension.
      // This solves the edge case where both dimensions of the selected icon exceed the dimensions of the constraint
      const adjustedDimensions = getDimensionsForAspectRatio({
        icon: selectedIcon,
        iconWidth,
        iconHeight
      });
      iconHeight = adjustedDimensions?.height;
      iconWidth = adjustedDimensions?.width;
    } else {
      x = 0;
    }

    dispatch(
      updatePosition({
        iconId: selectedIcon?.id,
        x,
        ...(y ? {y} : {}),
        ...(iconWidth ? {width: iconWidth} : {}),
        ...(iconHeight ? {height: iconHeight} : {}),
        viewId
      })
    );
  };

export const clearNonApplicableInkMatches = ({
  dispatch,
  state,
  removedIconIds
}) => {
  const inkMatches = getInkMatches(state);
  /*
    Pantone Match side effect:
      1. User selects icons to remove
      2. If an icon has a color in inkMatches
        - & no Upload Icon in the design remains with that color
      3. Remove that color from the color matches
  */
  if (!isEmpty(inkMatches)) {
    const uploadIcons = getUploadIcons(state);

    const removedUploadIds = uploadIcons
      .filter(icon => removedIconIds.includes(icon.id))
      .map(icon => icon.id);

    const existingUploadColors = getUploadColors(state).map(c => c.hexString);
    const removedUploadColors = getColorsByIconIds(state)(removedUploadIds).map(
      c => c.hexString
    );

    const newinkMatches = Object.keys(inkMatches).reduce((acc, match) => {
      if (
        existingUploadColors.filter(c => c === match).length ===
        removedUploadColors.filter(c => c === match).length
      ) {
        return acc;
      }
      return {...acc, [match]: inkMatches[match]};
    }, {});

    const inkMatchesChanged =
      Object.keys(newinkMatches).length !== Object.keys(inkMatches).length;

    if (inkMatchesChanged) {
      dispatch(updateInkMatches(newinkMatches));
    }
  }
};

export const remove =
  (payload = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const designId = getCurrentDesignId(state);

    // If a specific icon is provided, remove ony those, otherwise remove all selected.
    // This is handy when we have a bunch of icons selected and user press a remove button on one of them.
    const iconIds = payload.iconIds || getSelectedIconIds(state);
    const iconTypes = payload.iconTypes || getSelectedIconTypes(state);

    clearNonApplicableInkMatches({dispatch, state, removedIconIds: iconIds});

    dispatch({
      type: IconTypes.REMOVE,
      payload: {
        ...payload,
        iconIds,
        iconTypes,
        designId
      }
    });
  };

export const resetClipartColors = createAction(IconTypes.RESET_CLIPART_COLORS);

export const resize = createAction(IconTypes.RESIZE);

export const restore =
  ({sourceIconId, iconOptions}) =>
  (dispatch, getState) => {
    const targetIconId = uuid.v4();
    const state = getState();
    const designId = getCurrentDesignId(state);
    const viewId = selectedViewId(state);
    const productStyleType = getSelectedProductStyleType(state);
    const view = getView(state, {
      viewIdentifier: viewId
    });

    const uploadAutoSizing = isUploadAutoSizingEnabled(state);
    const isApparelWithUnsupportedView =
      productStyleType === STYLE_TYPE.APPAREL &&
      !['front', 'back'].includes(view.name);

    let iconAutoSizeDimension = {};
    if (uploadAutoSizing && !isApparelWithUnsupportedView) {
      const iconData = getIconById(state)(sourceIconId);
      const constraint = getViewConstraint(state)(view);
      const isFirstUpload = viewUploadedIcons(state).size === 0;

      iconAutoSizeDimension = getUploadAutoSizeDimension(
        constraint,
        iconData,
        productStyleType,
        isFirstUpload
      );
    }
    const options = {...iconOptions, ...iconAutoSizeDimension};
    const decorationOverrideColor = getDecorationOverrideColor(state);

    if (decorationOverrideColor || isAnchorLaserEngraved(state)) {
      options.color =
        decorationOverrideColor || getLaserEngravedDesignColor(state);
      options.singleColor = true;
      const testValue = signaler.featureFlag(UPLOAD_ONE_COLOR_FLAG);
      if (testValue === 'test') {
        options.contrast = DEFAULT_CONTRAST;
      }
    } else {
      // defaults
      options.color ??= INK_COLORS.Black;
      options.singleColor ??= false;
    }
    dispatch({
      type: IconTypes.RESTORE,
      payload: {
        designId,
        iconOptions: options,
        sourceIconId,
        targetIconId,
        viewId
      }
    });
    dispatch({
      type: IconTypes.ORDER_RECENT_UPLOADS,
      payload: {sourceIconId}
    });
  };

export const rotate = createAction(
  IconTypes.ROTATE,
  null,
  (data, iconType) => ({iconType})
);

export const setView = createAction(IconTypes.SET_VIEW, (icon, viewId) => {
  return {iconId: icon.id, iconType: icon.type, viewId};
});

export const updateHorizontalFlip = createAction(
  IconTypes.UPDATE_HORIZONTAL_FLIP,
  null,
  (data, iconType) => ({iconType})
);

export const updateVerticalFlip = createAction(
  IconTypes.UPDATE_VERTICAL_FLIP,
  null,
  (data, iconType) => ({iconType})
);

export const updateSingleColor = (data, iconType) => (dispatch, getState) => {
  const state = getState();
  const lastIconColor = getLastIconColor(state);

  // warn user if they are about to delete their ink matches
  const inkMatches = getInkMatches(state);
  if (!isEmpty(inkMatches)) {
    return dispatch(
      errorNotification({
        key: COLOR_CHANGE_PANTONE_WARNING,
        attemptedMonochrome: [data, iconType]
      })
    );
  }

  dispatch({
    type: IconTypes.UPDATE_SINGLE_COLOR,
    payload: {
      ...data,
      color: lastIconColor || data.defaultIconColor
    },
    meta: {
      iconType
    }
  });
};

export const updateSingleColorAndContrast =
  (data, iconType) => (dispatch, getState) => {
    const state = getState();
    const lastIconColor = getLastIconColor(state);

    // warn user if they are about to delete their ink matches
    const inkMatches = getInkMatches(state);
    if (!isEmpty(inkMatches)) {
      return dispatch(
        errorNotification({
          key: COLOR_CHANGE_PANTONE_WARNING,
          attemptedMonochrome: [data, iconType]
        })
      );
    }

    dispatch({
      type: IconTypes.UPDATE_SINGLE_COLOR_AND_CONTRAST,
      payload: {
        ...data,
        color: lastIconColor || data.defaultIconColor
      },
      meta: {
        iconType
      }
    });
  };

export const updateContrast = createAction(IconTypes.UPDATE_CONTRAST);

export const updateInvertContrast = createAction(
  IconTypes.UPDATE_INVERT_CONTRAST
);

export const updatePosition = createAction(IconTypes.UPDATE_POSITION);

export const toggleInvertedClipart = createAction(
  IconTypes.TOGGLE_INVERTED_CLIPART
);
export const changeThreshold = createAction(CanvasTypes.CHANGE_THRESHOLD);

export const alignCenter = createAction(IconTypes.ALIGN_CENTER);

export const alignMiddle = createAction(IconTypes.ALIGN_MIDDLE);

export const alignLeft = createAction(IconTypes.ALIGN_LEFT);

export const alignRight = createAction(IconTypes.ALIGN_RIGHT);

export const alignTop = createAction(IconTypes.ALIGN_TOP);

export const alignBottom = createAction(IconTypes.ALIGN_BOTTOM);

export const distributeHorizontal = createAction(
  IconTypes.DISTRIBUTE_HORIZONTAL
);

export const distributeVertical = createAction(IconTypes.DISTRIBUTE_VERTICAL);
